import { inject } from '@angular/core';
import {
  Router,
  CanActivateFn,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { map, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { CommonService } from '../services/common.service';

export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const commonService = inject(CommonService);

  // Check if user is already authenticated without making API call
  if (authService.isAuthenticated()) {
    return true;
  }

  // If not authenticated, try to authenticate
  return authService.authenticate().pipe(
    take(1),
    map((isAuthenticated) => {
      if (isAuthenticated) {
        // The dynamic product routes only exist once authenticate() has run, so
        // the URL the user asked for may not have matched anything a moment ago.
        // Re-navigate now that the routes are loaded, preferring the entry URL
        // over `state.url` — the latter has already been through redirect
        // resolution and so reads as the default product for a deep link.
        const target = commonService.consumeRedirectUrl(state.url);
        if (target !== state.url) {
          router.navigateByUrl(target);
          return false;
        }
        if (state.url && state.url !== '/app' && state.url !== '/app/') {
          router.navigateByUrl(state.url);
          return false;
        }
        return true;
      }


      // Remember where the user was headed so login can send them back.
      // `state.url` is only a fallback: the router resolves redirects before
      // guards run, so on a deep link whose dynamic route isn't loaded yet it
      // already reads as the default product. The entry URL captured at
      // bootstrap is the real destination, so it wins.
      commonService.captureRedirectUrl(state.url, false);

      // Keep the query param as a reload-survivable mirror of the signal.
      const returnUrl = commonService.redirectUrl();

      return router.createUrlTree(['/login'], {
        queryParams: returnUrl ? { returnUrl } : {},
      });
    })
  );
};
